/**
 * `@ronceray/catalog` — the standards data, typed, versioned and hashed (ARCHITECTURE.md §4.4).
 *
 * One source of truth for the tables the client needs for its dropdowns and the server needs for
 * validation and calculation. The catalog is **public data and ships to the browser**; the engine
 * never does (PRD Technical Considerations, enforced by the CI canary scan).
 *
 * This entry point is browser-safe: data, types and a pure function. `CATALOG_HASH` lives in
 * `./hash` instead, because computing it needs `node:crypto` — see that file's header for the
 * reasoning, and README.md for how consumers import it.
 *
 * The package has zero runtime dependencies and imports nothing platform-specific (FR-2's sibling
 * rule for the catalog: nothing in `engine`, `catalog` or `contract` may reach for a Cloudflare
 * API, or the portability table in ARCHITECTURE.md §12 stops being true).
 */

export { CATALOG } from './catalog.generated';

export { canonicalStringify, CanonicalisationError } from './canonical';

export type {
  BucklingCurve,
  Catalog,
  Fabrication,
  HollowSection,
  KdefRow,
  KmodRow,
  LoadScheme,
  ProfileFamily,
  SteelGrade,
  SteelProfile,
  TimberClass,
} from './types';

/**
 * Version of the catalog **data**, stamped onto every calculation result and every saved case.
 *
 * Bump it on **any** data change, without exception: a corrected `Wply` changes an engineering
 * result exactly like a formula change does, and the stamp is what lets someone in 2036 say which
 * numbers produced a note. Regenerating the file without bumping this is the one failure mode the
 * whole versioning chain cannot recover from.
 *
 * The scheme is date-based (`YYYY.MM`), not semver — catalog data has editions, not features, and
 * a table taken from a 2026-08 reading of EN 1993-1-1 / EN 338 / EN 14080 is best identified by
 * when it was read. This resolves **PRD Open Question 4** ("confirm the scheme is date-based
 * rather than semver before it is stamped onto results permanently"): confirmed date-based for P0,
 * initial value `"2026.08"`, matching the ARCHITECTURE.md §4.5 response-envelope example.
 */
export const CATALOG_VERSION = '2026.08';
